import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { IMAGE_MIME_TYPES_WITH_PDF_AND_DOC } from "@/components/upload/constants"
import useHandleImageFileUpload from "@/components/upload/hooks/useHandleFileUpload"
import type { FieldGroupEnum } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { PurchaseOrderFileForm } from "@/screens/Procurement/PurchaseOrder/components/PurchaseOrderFiles/PurchaseOrderFileForm"
import type {
  PurchaseOrderFileFormValues,
  PurchaseOrderFileValues,
} from "@/screens/Procurement/PurchaseOrder/components/PurchaseOrderFiles/schemas/PurchaseOrderFileSchema"
import { PurchaseOrderFileFormSchema } from "@/screens/Procurement/PurchaseOrder/components/PurchaseOrderFiles/schemas/PurchaseOrderFileSchema"
import { BLANK_PURCHASE_ORDER_FILE } from "@/screens/Procurement/PurchaseOrder/constants"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface PurchaseOrderFileDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  orderId: string
  isQuote?: boolean
}

export const PurchaseOrderFileDialog: FC<PurchaseOrderFileDialogProps> = ({
  open,
  onClose,
  onSuccess,
  orderId,
  isQuote = false,
}) => {
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const methods = useForm<PurchaseOrderFileFormValues>({
    defaultValues: { purchaseOrderFiles: [BLANK_PURCHASE_ORDER_FILE] },
    resolver: zodResolver(PurchaseOrderFileFormSchema),
    mode: "onChange",
  })
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = methods

  const { fields: PurchaseOrderFileFields, append } = useFieldArray<PurchaseOrderFileFormValues>({
    control,
    name: "purchaseOrderFiles",
  })

  const { uploadFile } = useHandleImageFileUpload({
    referenceTableName: ReferenceTableName.Order,
    allowedFileTypes: IMAGE_MIME_TYPES_WITH_PDF_AND_DOC,
  })

  const handleAddAnotherFile = (): void => {
    append(BLANK_PURCHASE_ORDER_FILE)
  }

  const handleSubmitData = async (data: PurchaseOrderFileFormValues) => {
    setIsUploading(true)
    try {
      const filesToUpload = data.purchaseOrderFiles

      await Promise.all(
        filesToUpload.map((fileData: PurchaseOrderFileValues) =>
          uploadFile({
            file: fileData.file,
            type: fileData.fileType as FieldGroupEnum,
            referenceId: orderId,
            label: fileData.description,
          })
        )
      )

      notify({
        message: "Purchase order files attached successfully",
        type: NotificationType.Success,
      })

      onSuccess()
    } catch {
      notify({
        message: "Purchase order files were not attached",
        type: NotificationType.Error,
      })
    }
    setIsUploading(false)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth data-testid='product-file-dialog'>
      <DialogHeader title={isQuote ? "Attach File to Quote" : "Attach File to PO"} onClose={onClose} />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <PurchaseOrderFileForm fields={PurchaseOrderFileFields} />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Box className='flex justify-end'>
          <Button appearance='text' onClick={handleAddAnotherFile} startIcon={<AddOutlined />} disabled={!isValid}>
            Add Another File
          </Button>
          <Button loading={isUploading} onClick={handleSubmit(handleSubmitData)} type='submit' disabled={!isValid}>
            Done
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
